import { useEffect } from "react";
import { useLocation } from "react-router-dom";

interface SEOProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  type?: string;
  noIndex?: boolean;
}

const defaultMeta = {
  title: "Floop - Générateur d'Images IA | Créez des Visuels Époustouflants",
  description: "Transformez vos idées en images HD et 4K en quelques secondes avec Floop, l'IA créative africaine. Gratuit pour commencer, sans carte bancaire. Essayez maintenant !",
  keywords: "générateur image IA, création image intelligence artificielle, text to image français, AI image generator, Floop IA, créer images IA gratuit, génération visuel IA, outil design IA, retouche photo IA, illustration IA",
  image: "https://floop.sbs/og-image.png",
  type: "website",
};

export function SEO({
  title,
  description,
  keywords,
  image,
  type,
  noIndex = false,
}: SEOProps) {
  const location = useLocation();
  
  const fullTitle = title 
    ? `${title} | Floop` 
    : defaultMeta.title;
  const fullDescription = description || defaultMeta.description;
  const fullKeywords = keywords || defaultMeta.keywords;
  const fullImage = image || defaultMeta.image;
  const fullType = type || defaultMeta.type;
  const canonicalUrl = `https://floop.sbs${location.pathname}`;

  useEffect(() => {
    // Update document title
    document.title = fullTitle;

    // Update meta tags
    const updateMeta = (name: string, content: string, isProperty = false) => {
      const attr = isProperty ? "property" : "name";
      let element = document.querySelector(`meta[${attr}="${name}"]`);
      if (element) {
        element.setAttribute("content", content);
      } else {
        element = document.createElement("meta");
        element.setAttribute(attr, name);
        element.setAttribute("content", content);
        document.head.appendChild(element);
      }
    };

    // Standard meta tags
    updateMeta("description", fullDescription);
    updateMeta("keywords", fullKeywords);
    
    // Robots
    if (noIndex) {
      updateMeta("robots", "noindex, nofollow");
    } else {
      updateMeta("robots", "index, follow, max-image-preview:large");
    }

    // Open Graph
    updateMeta("og:title", fullTitle, true);
    updateMeta("og:description", fullDescription, true);
    updateMeta("og:type", fullType, true);
    updateMeta("og:image", fullImage, true);
    updateMeta("og:url", canonicalUrl, true);
    updateMeta("og:site_name", "Floop", true);
    updateMeta("og:locale", "fr_FR", true);

    // Twitter Card
    updateMeta("twitter:card", "summary_large_image");
    updateMeta("twitter:title", fullTitle);
    updateMeta("twitter:description", fullDescription);
    updateMeta("twitter:image", fullImage);
    updateMeta("twitter:site", "@FloopAI");

    // Canonical URL
    let canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) {
      canonical.setAttribute("href", canonicalUrl);
    } else {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      canonical.setAttribute("href", canonicalUrl);
      document.head.appendChild(canonical);
    }

    return () => {
      // Cleanup is optional - tags persist for SPA navigation
    };
  }, [fullTitle, fullDescription, fullKeywords, fullImage, fullType, canonicalUrl, noIndex]);

  // JSON-LD Structured Data
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "Floop",
    "alternateName": "Floop IA",
    "description": fullDescription,
    "url": canonicalUrl,
    "applicationCategory": "MultimediaApplication",
    "operatingSystem": "Web",
    "browserRequirements": "Requires JavaScript",
    "softwareVersion": "2.0",
    "offers": {
      "@type": "AggregateOffer",
      "lowPrice": "0",
      "highPrice": "49990",
      "priceCurrency": "XAF",
      "offerCount": "4"
    },
    "creator": {
      "@type": "Organization",
      "name": "Floop",
      "url": "https://floop.sbs",
      "logo": "https://floop.sbs/logo.png",
      "contactPoint": {
        "@type": "ContactPoint",
        "telephone": "+237697747365",
        "contactType": "customer service",
        "availableLanguage": ["French", "English"]
      },
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Bertoua",
        "addressCountry": "CM"
      }
    },
    "aggregateRating": {
      "@type": "AggregateRating",
      "ratingValue": "4.9",
      "reviewCount": "2847",
      "bestRating": "5"
    }
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
    />
  );
}
